import styled from "@emotion/styled";
import type { CSSProperties, ReactNode } from "react";

import { useFormatMessage } from "@skilitics/localization";

import { Button, PrimaryButton } from "../buttons";
import { Loading, type LoadingProps } from "../common";

export const WizardPageTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSizeLarge};
  color: ${({ theme }) => theme.primaryStrong};
`;

const CompleteButtonContainer = styled.div`
  display: flex;
  gap: 8px;

  place-items: center;
`;

type ButtonProps = {
  disabled?: boolean;
  style?: CSSProperties;
};

type Props = ButtonProps & {
  busy: boolean;
  label: ReactNode;
  loadingProps?: LoadingProps;
};

export const CompleteButton = ({ disabled, busy, label, style, loadingProps }: Props) => (
  <PrimaryButton disabled={disabled || busy} style={style}>
    <CompleteButtonContainer>
      {busy && <Loading size={16} fillColor={"white"} {...loadingProps} />}
      {label}
    </CompleteButtonContainer>
  </PrimaryButton>
);

export const CancelButton = ({ disabled, style, onClick }: ButtonProps & { onClick?: () => void }) => {
  const formatMessage = useFormatMessage(formatStrings);

  return (
    <Button key="cancel" color="inherit" disabled={disabled} onClick={onClick} style={style} data-test="cancel-button">
      {formatMessage("cancel")}
    </Button>
  );
};

export const BackButton = ({ style }: ButtonProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  return (
    <Button key="back" style={style} data-test="back-button">
      {formatMessage("back")}
    </Button>
  );
};

export const NextButton = ({ style }: ButtonProps) => {
  const formatMessage = useFormatMessage(formatStrings);

  return (
    <Button key="next" style={style} variant="contained" data-test="next-button">
      {formatMessage("next")}
    </Button>
  );
};

const formatStrings = {
  back: {
    en: "Back",
  },
  cancel: {
    en: "Cancel",
  },
  next: {
    en: "Next",
  },
};
